import React, { Component } from "react";
import PropTypes from "prop-types";
import Label from "./Label";
import map from "lodash/map";
import uniq from "lodash/uniq";
import get from "lodash/get";
import assign from "lodash/assign";
import startCase from "lodash/startCase";
import IconButton from "components/IconButton";
import { getIconForUID } from "components/FormerEditor/utils";
import { INFO_MODAL_ICON, AUTO_ATTRS, BUTTON_HOVER_COLORS } from "./constants";

const DEFAULT_CLASSES = {
  container: "",
  input:
    "border-box input-reset ba br2 b--black-20 pa2 mb2 db w-100 pointer shadow-0"
};
const DEFAULT_ICON_STYLE = { right: 6 };
const TEXT_TYPE = "text";

/**
 * options = [{uid: 'uid': label: 'Some option'}]
 */
class Select extends Component {
  constructor() {
    super();
    this._component = null;
  }

  stageActionButton() {
    const { disabled, onChange, component, stage, stateKey } = this.props;
    const actionProps = {
      disabled,
      onClick: () => {
        if (onChange) {
          onChange(stateKey, component)(stage);
        } else if (this._component && !disabled) {
          this._component.value = stage;
        } else {
          console.log("** No ability to use stage");
        }
      },
      iconType: "magic",
      customClasses: {
        button: "ma0 bg-white pointer br2 ba br2 bw1 b--black-30 mb2 br--right"
      },
      colors: BUTTON_HOVER_COLORS
    };

    return <IconButton {...actionProps} />;
  }

  stageComp() {
    const { label, stage, customClasses } = this.props;
    const classes = assign({}, DEFAULT_CLASSES, customClasses);
    const stageInput = {
      "disabled": true,
      "value": stage,
      "className": `field__input ${classes.input} br--left`,
      "type": TEXT_TYPE,
      "aria-describedby": "label-desc",
      ...AUTO_ATTRS
    };

    return (
      <div key={`${label}-stage`} className="flex flex-row">
        <input {...stageInput} />
        {this.stageActionButton()}
      </div>
    );
  }

  optionNodes(options) {
    const transformer = this.props.labelTransform || startCase;
    if (typeof options[0] === "object") {
      return options.map(({ uid, label, disabled }, optIx) => {
        const copy = transformer(label);
        return (
          <option key={optIx} disabled={disabled} value={uid}>
            {copy}
          </option>
        );
      });
    } else {
      return map(uniq(options), (opt, optIx) => {
        const copy = transformer(opt);
        const optProps = {
          key: optIx,
          value: opt
        };
        return <option {...optProps}>{copy}</option>;
      });
    }
  }

  render() {
    const {
      label,
      value,
      options,
      defaultValue,
      customClasses,
      onChange,
      setRef,
      disabled,
      stage,
      validation,
      stateKey,
      showInfo,
      component,
      iconStyle
    } = this.props;
    const classes = assign({}, DEFAULT_CLASSES, customClasses);
    const selectValue = value || defaultValue;
    const nodes = options && this.optionNodes(options);
    const Icon = getIconForUID("down");

    const selectProps = {
      "id": label,
      disabled,
      "key": "input-select",
      "className": `field__input ${classes.input}`,
      "type": "text",
      "aria-describedby": "name-desc",
      "ref": (inputComp) => {
        this._component = inputComp;
      }
    };

    if (onChange) {
      selectProps.value = selectValue;
      selectProps.onChange = onChange(stateKey, component);
    } else {
      selectProps.defaultValue = selectValue;
    }

    if (setRef) {
      selectProps.ref = (comp) => {
        setRef(stateKey, comp);
      };
    }

    let labelComp;
    if (label) {
      const labelProps = {
        label,
        error: get(validation, "error", false)
      };
      labelComp = <Label {...labelProps} />;
    }
    let showInfoComp;
    if (showInfo) {
      const infoIconProps = {
        onClick: showInfo,
        iconType: "info",
        customClasses: {
          button: "flex ma0 bg-white pointer bn"
        },
        ...INFO_MODAL_ICON
      };
      if (infoIconProps.iconProps && infoIconProps.iconProps.size) {
        infoIconProps.iconProps.size = 20;
      }

      showInfoComp = <IconButton {...infoIconProps} />;
    }

    const inputComps = [
      <select key="select" {...selectProps}>
        {nodes}
      </select>
    ];

    if (stage && selectProps.value.toLowerCase() !== stage.toLowerCase()) {
      inputComps.push(this.stageComp());
    }

    return (
      <div className={classes.container}>
        {(labelComp || showInfoComp) && (
          <div className="flex flex-row">
            {labelComp}
            {showInfoComp}
          </div>
        )}
        <div className="relative h-100">
          <div
            style={iconStyle || DEFAULT_ICON_STYLE}
            className="absolute top-0 bottom-0 flex items-center justify-center"
          >
            <Icon size={14} />
          </div>
          {inputComps}
        </div>
      </div>
    );
  }
}

Select.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  stateKey: PropTypes.string,
  customClasses: PropTypes.object,
  component: PropTypes.string,
  disabled: PropTypes.bool,
  validation: PropTypes.object,
  iconStyle: PropTypes.object,
  stage: PropTypes.any,
  // Expected to be an Array of primitive values
  // - not a collection of objects
  options: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ),
  onChange: PropTypes.func,
  showInfo: PropTypes.func,
  setRef: PropTypes.func,
  labelTransform: PropTypes.func
};

Select.defaultProps = {
  // onChange: (key, component) => {
  //   return (evt) => {
  //     console.log('**** Select value', evt.currentTarget.value)
  //   }
  // }
};

export default Select;
